// Staff sign-in (spec/13 §1): a code to the phone, or a password — then the second factor where the
// account has one (spec/07 §6.6). The phone is offered only while phone sign-in is on — the Platform's switch,
// `auth.phoneSignIn`, off since the owner's instruction of 2026-10-08 (spec/07 §3.1): until `GET /v1/meta` says
// it is on, the screen is the password form alone, with no choice of method and no phone field. Everything goes through better-auth's own endpoints, in the
// contract (spec/08 §4.1); the session is its cookie. Passkeys (F-IAM-06) join when their endpoints
// join the contract.
//
// **Continue with Google, Discord or Steam** (F-IAM-03 … F-IAM-05, ADR-0046) on the organisation console, each where
// the deployment has it switched on (one full-width, two or three as a row of marks): a press goes straight to the
// provider; an account with it signs in, and a provider account with no Clavian account comes back
// `?error=CONSENT_REQUIRED`, answered with "Create your Clavian account" (the terms, then the provider again). The
// HQ console is invitation-first and staff-only, and offers none.
//
// Once signed in, the visitor goes back where they were going — or, with an invitation waiting in this
// browser, to accept it (`invitation-token.ts`).

import type { SocialProvider } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, RadioGroup } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { queryKeys } from '../api/queries';
import { requestShiftPrompt } from '../org/shifts/shift-prompt';
import { useSurface } from '../surface';
import { AuthLayout } from './AuthLayout';
import { useAuthFailure } from './auth-errors';
import { Failure, TextInput } from './form-controls';
import { SecondFactorForm } from './SecondFactorForm';
import { afterSignIn } from './invitation-token';
import { usePhoneSignIn } from './sign-in-methods';
import { ContinueWithProviders, CreateAccountDialog, isSocialProvider, socialErrorKey } from './social';

type Step = { readonly kind: 'identify' } | { readonly kind: 'code'; readonly phoneNumber: string } | { readonly kind: 'second-factor' };

/** E.164: what the contract's `PhoneNumber` accepts (spec/07 §2.1 "E.164 only"). */
const E164 = /^\+[1-9]\d{1,14}$/;
const compact = (phone: string) => phone.replace(/[\s()-]+/g, '');

export function SignInPage() {
  const { t } = useTranslation('console');
  const surface = useSurface();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: '/sign-in' });
  const phoneOn = usePhoneSignIn();
  const [chosen, setMethod] = useState<'phone' | 'password'>('phone');
  const method = phoneOn ? chosen : 'password';
  const [step, setStep] = useState<Step>({ kind: 'identify' });

  const signedIn = async () => {
    // "Start your shift" opens on the first venue screen after signing in (ADR-0031).
    requestShiftPrompt();
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await queryClient.refetchQueries({ queryKey: queryKeys.session });
    await navigate({ to: afterSignIn(search.redirect) });
  };
  const fromProvider = socialErrorKey(search.error);
  const returned = isSocialProvider(search.provider) ? search.provider : undefined;
  const providerName = t(`social.provider.${returned ?? 'google'}`);
  // A provider account with no Clavian account yet: "Create your Clavian account", the terms, then the same provider.
  const [creating, setCreating] = useState<SocialProvider | null>(
    surface.id === 'org' && fromProvider === 'social.error.noAccount' && returned !== undefined ? returned : null,
  );
  const trip = {
    landing: afterSignIn(search.redirect),
    onError: search.redirect === undefined ? '/sign-in' : `/sign-in?redirect=${encodeURIComponent(search.redirect)}`,
  };

  let body: ReactNode;
  if (step.kind === 'second-factor') {
    body = <SecondFactorForm intro={t('secondFactor.intro')} onVerified={signedIn} />;
  } else if (step.kind === 'code' && phoneOn) {
    body = (
      <PhoneCodeForm
        phoneNumber={step.phoneNumber}
        onSignedIn={signedIn}
        onChangeNumber={() => setStep({ kind: 'identify' })}
      />
    );
  } else {
    body = (
      <>
        {phoneOn ? (
          <RadioGroup
            label={t('signIn.method.legend')}
            orientation="horizontal"
            value={method}
            onValueChange={(value) => setMethod(value === 'password' ? 'password' : 'phone')}
            options={[
              { value: 'phone', label: t('signIn.method.phone') },
              { value: 'password', label: t('signIn.method.password') },
            ]}
          />
        ) : null}
        {method === 'phone' ? (
          <PhoneNumberForm onSent={(phoneNumber) => setStep({ kind: 'code', phoneNumber })} />
        ) : (
          <PasswordForm onSignedIn={signedIn} onSecondFactor={() => setStep({ kind: 'second-factor' })} />
        )}
        {/* Sign-up and Google are the organisation console's own (F-IAM-02, F-IAM-03); the HQ console is
            staff-only, invitation-first, so it offers neither. */}
        {surface.id === 'org' ? (
          <>
            {fromProvider === undefined || creating !== null ? null : (
              <div className="console-alert" role="alert">
                <p>{t(fromProvider, { provider: providerName })}</p>
                {fromProvider === 'social.error.noAccount' ? (
                  <p>
                    <Link to="/sign-up" search={search.redirect === undefined ? {} : { redirect: search.redirect }}>
                      {t('social.error.signUpInstead')}
                    </Link>
                  </p>
                ) : null}
              </div>
            )}
            <ContinueWithProviders {...trip} />
            <CreateAccountDialog provider={creating} {...trip} onClose={() => setCreating(null)} />
            <p className="auth__alt">
              <Link to="/sign-up" search={search.redirect === undefined ? {} : { redirect: search.redirect }}>
                {t('signIn.newHere')}
              </Link>
            </p>
          </>
        ) : null}
      </>
    );
  }

  const subtitle = surface.id === 'hq' ? t('hq:signIn.intro') : t('signIn.intro');
  const footer = surface.id === 'hq' ? t('hq:signIn.footer') : t('signIn.footer');

  return (
    <AuthLayout
      title={step.kind === 'second-factor' ? t('secondFactor.title') : t('signIn.title')}
      subtitle={step.kind === 'identify' ? subtitle : undefined}
      footer={footer}
    >
      {body}
    </AuthLayout>
  );
}

function PhoneNumberForm({ onSent }: { readonly onSent: (phoneNumber: string) => void }) {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure();
  const api = useApi();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ phoneNumber: string }>({ defaultValues: { phoneNumber: '' } });

  const submit = form.handleSubmit(async ({ phoneNumber }) => {
    setFailure(null);
    try {
      await api.sendPhoneNumberOTP({ body: { phoneNumber: compact(phoneNumber) } });
      onSent(compact(phoneNumber));
    } catch (error) {
      setFailure(failureText(error));
    }
  });

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="phoneNumber"
        label={t('signIn.phone.number')}
        description={t('signIn.phone.numberHint')}
        type="tel"
        autoComplete="tel"
        rules={{
          required: t('signIn.required'),
          validate: (value: string) => E164.test(compact(value)) || t('signIn.invalidPhone'),
        }}
      />
      <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
        {t('signIn.phone.send')}
      </Button>
    </form>
  );
}

function PhoneCodeForm({
  phoneNumber,
  onSignedIn,
  onChangeNumber,
}: {
  readonly phoneNumber: string;
  readonly onSignedIn: () => Promise<void>;
  readonly onChangeNumber: () => void;
}) {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure();
  const api = useApi();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submit = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    try {
      await api.verifyPhoneNumber({ body: { phoneNumber, code: code.trim() } });
      await onSignedIn();
    } catch (error) {
      setFailure(failureText(error));
    }
  });

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate>
      <p role="status">{t('signIn.phone.sent', { phone: phoneNumber })}</p>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="code"
        label={t('signIn.phone.code')}
        description={t('signIn.phone.codeHint', { phone: phoneNumber })}
        inputMode="numeric"
        autoComplete="one-time-code"
        numeric
        rules={{ required: t('signIn.required') }}
      />
      <div className="console-form__actions">
        <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
          {t('signIn.phone.verify')}
        </Button>
        <Button variant="ghost" onClick={onChangeNumber}>
          {t('signIn.phone.change')}
        </Button>
      </div>
    </form>
  );
}

function PasswordForm({ onSignedIn, onSecondFactor }: { readonly onSignedIn: () => Promise<void>; readonly onSecondFactor: () => void }) {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure();
  const api = useApi();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ identifier: string; password: string }>({ defaultValues: { identifier: '', password: '' } });

  const submit = form.handleSubmit(async ({ identifier, password }) => {
    setFailure(null);
    try {
      const trimmed = identifier.trim();
      const result = trimmed.includes('@')
        ? await api.signInEmail({ body: { email: trimmed, password } })
        : await api.signInUsername({ body: { username: trimmed, password } });
      if (result.twoFactorRedirect === true) onSecondFactor();
      else await onSignedIn();
    } catch (error) {
      setFailure(failureText(error));
    }
  });

  return (
    <form className="console-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="identifier"
        label={t('signIn.password.identifier')}
        autoComplete="username"
        rules={{ required: t('signIn.required') }}
      />
      <TextInput
        control={form.control}
        name="password"
        label={t('signIn.password.password')}
        type="password"
        autoComplete="current-password"
        rules={{ required: t('signIn.required') }}
      />
      <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
        {t('signIn.password.submit')}
      </Button>
    </form>
  );
}

